import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Img,
    AspectRatio,
    Button,
    Flex,
    Stack,
    Fade,
    Box,
    Text,
    useMultiStyleConfig,
} from '@chakra-ui/react'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {CheckIcon} from '../icons/others'
//import OmnibusModalPrice from '../display-price/omnibus/omnibus-modal-price'
import useProductData from '../../hooks/use-product-data'
import {PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID} from '@salesforce/retail-react-app/app/constants'
import Link from '@salesforce/retail-react-app/app/components/link'
import {findImageGroupBy} from '../../utils/image-groups-utils'
import AddToCartButton from '../add-to-cart-button'
import {ADD_TO_CART_ERROR_KEYS, ADD_TO_CART_ERROR_MESSAGES} from '../../constants'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import CurrentPrice from '../display-price/current-price'
import {remCalc} from '../../theme/_utils'

const UniversalFeedbackProducts = ({product, isPurchaseBuy = false}) => {
    const styles = useMultiStyleConfig('Modal')
    const intl = useIntl()
    const errorContainerRef = useRef(null)
    const [showErrorMessage, setShowErrorMessage] = useState(0)
    const [addedStates, setAddedStates] = useState({})
    const [preventFocus, setPreventFocus] = useState(false)
    const {productUrl} = useProductData(product, PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID)
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')

    const pricesFirst = product?.c_pricesFirst && JSON.parse(product?.c_pricesFirst)
    const priceFirst =
        pricesFirst &&
        Object.values(pricesFirst).reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0)
    const pricesRecurring = product?.c_pricesRecurring && JSON.parse(product?.c_pricesRecurring)
    const priceRecurring =
        pricesRecurring &&
        Object.values(pricesRecurring).reduce(
            (sum, item) => sum + (item.price || 0) * item.quantity,
            0,
        )

    const handleClick = async (itemsAdded) => {
        if (!itemsAdded) return
        setAddedStates((prev) => {
            const updatedState = {...prev}
            itemsAdded?.forEach((product) => {
                updatedState[product?.id] = true
            })
            return updatedState
        })
    }

    const image =
        product?.image?.disBaseLink ||
        product?.image?.link ||
        findImageGroupBy(product?.imageGroups, {
            viewType: 'large',
            selectedVariationAttributes: product?.variationValues,
        })?.images?.[0]?.link

    useEffect(() => {
        if (preventFocus) {
            setPreventFocus(false)
        }
    }, [preventFocus])

    const isSubscription = product?.c_isSubscription === 'true'

    return (
        <Stack {...styles.recommendedProductGap} data-testid="recommended-product">
            <Flex {...styles.recommendedProductGapDouble}>
                <Link to={productUrl}>
                    <AspectRatio ratio={1} {...styles.recommendedProductImage}>
                        <Img
                            maxWidth={'fit-content'}
                            src={image}
                            alt={product?.c_variantIdentifier}
                        />
                    </AspectRatio>
                </Link>
                <Stack {...styles.recommendedProductGap}>
                    <Text
                        noOfLines="2"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        textStyle={isPurchaseBuy ? 'body-medium' : 'body-small'}
                    >
                        {product?.c_variantIdentifier}
                    </Text>
                    <Flex {...styles.recommendedProductGap}>
                        {priceRecurring && (
                            <Text textDecoration={'line-through'} textStyle={'body-small'}>
                                <FormattedNumber
                                    minimumFractionDigits={2}
                                    maximumFractionDigits={2}
                                    value={priceRecurring}
                                />
                            </Text>
                        )}
                        <CurrentPrice
                            labelForA11y=""
                            price={priceFirst ?? product?.priceperUnit ?? product?.price ?? 0}
                            as="b"
                            currency={product?.currency}
                            isRange={false}
                            color="text.default"
                            textStyle={'body-small'}
                            fontWeight={400}
                            alignSelf="center"
                        />
                        {priceFirst &&
                            (product?.c_subFrequency === '1' ? (
                                <Text textStyle={'body-small'}>
                                    /{' '}
                                    <FormattedMessage
                                        id="subscriptions.labels.month"
                                        defaultMessage="month"
                                    />
                                </Text>
                            ) : (
                                <Text textStyle={'body-small'}>
                                    / {product?.c_subFrequency}{' '}
                                    <FormattedMessage
                                        id="subscriptions.labels.months"
                                        defaultMessage="months"
                                    />
                                </Text>
                            ))}
                    </Flex>
                    {isSubscription && product?.c_subscriptionShortDescription && (
                        <Box
                            {...styles.longDescriptionSubscription}
                            dangerouslySetInnerHTML={{
                                __html: product.c_subscriptionShortDescription,
                            }}
                        />
                    )}
                </Stack>
            </Flex>
            {showErrorMessage ? (
                <Box ref={errorContainerRef}>
                    <Fade in={true}>
                        <Text color="text.danger" fontWeight={600} marginBottom={8}>
                            {showErrorMessage ===
                            ADD_TO_CART_ERROR_KEYS.INVENTORY_REMAINING_FOR_PRODUCT
                                ? intl.formatMessage(
                                      ADD_TO_CART_ERROR_MESSAGES.INVENTORY_REMAINING_FOR_PRODUCT,
                                      {
                                          stockLevel: product?.c_extendVariant?.stock || 0,
                                          productName: product.productName,
                                      },
                                  )
                                : intl.formatMessage(
                                      ADD_TO_CART_ERROR_MESSAGES.NO_VARIANT_SELECTED,
                                  )}
                        </Text>
                    </Fade>
                </Box>
            ) : (
                <></>
            )}
            <Flex>
                {addedStates[product.id] ? (
                    <Button
                        //onClick={() => handleClick(product.id)}
                        variant="inverted-pressed"
                        {...styles.recommendedAdd}
                        w="full"
                        sx={{
                            ...styles.recommendedAdd,
                            maxH: isPurchaseBuy
                                ? {base: remCalc(40), lg: remCalc(40)}
                                : {base: remCalc(33), lg: remCalc(40)},
                            height: isPurchaseBuy
                                ? {base: remCalc(40), lg: remCalc(40)}
                                : {base: remCalc(33), lg: remCalc(40)},
                        }}
                    >
                        <>
                            <CheckIcon {...styles.checkIconBox} />
                            <Text data-testid="added-to-cart">
                                {intl.formatMessage({
                                    defaultMessage: 'Added',
                                    id: 'add_to_cart_modal.label.add-to-cart-added',
                                })}
                            </Text>
                        </>
                    </Button>
                ) : (
                    !notShoppableCountry && (
                        <AddToCartButton
                            product={product}
                            setShowErrorMessage={setShowErrorMessage}
                            errorContainerRef={errorContainerRef}
                            showUniversalFeedback={false}
                            universalFeedback={true}
                            isSubscription={isSubscription}
                            callback={() => handleClick([product])}
                            isPurchaseBuy={isPurchaseBuy}
                        />
                    )
                )}
            </Flex>
        </Stack>
    )
}

UniversalFeedbackProducts.propTypes = {
    badgeDetails: PropTypes.array,
    product: PropTypes.object,
    isPurchaseBuy: PropTypes.bool,
}

export default UniversalFeedbackProducts
